import { z } from 'zod';
import { IdSchema } from './common.js';

/**
 * Shifts and attendance in the agent workspace (docs/36 §8, migration 0107).
 *
 * A shift is a person at work: it starts when they log on and ends when they log off, or when
 * the workspace stops saying it is there. Within it one state is open at a time, so a day's
 * states add up to the shift exactly -- which is what makes "productive hours" a number anybody
 * can check rather than an estimate.
 */

/** What an agent is doing. `on_call` is set by the workspace when its phone is on a call. */
export const AGENT_STATES = ['ready', 'not_ready', 'on_call', 'wrap_up'] as const;
export type AgentState = (typeof AGENT_STATES)[number];

export const AgentStateSchema = z.enum(AGENT_STATES);

/**
 * A reason is required to go not ready, and forbidden otherwise.
 *
 * Required because "not ready" without one is the state everybody's day disappears into: a
 * report that says four hours unaccounted answers nothing, and asking at the moment it happens
 * costs one click.
 */
export const AgentStateInputSchema = z.object({
  state: AgentStateSchema,
  reason: z.string().trim().max(32).nullable().optional().transform((v) => v ?? null),
}).strict().superRefine((v, ctx) => {
  if (v.state === 'not_ready' && !v.reason) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], message: 'Choose a reason.' });
  }
  if (v.state !== 'not_ready' && v.reason) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reason'], message: 'Only a pause has a reason.' });
  }
});
export type AgentStateInput = z.infer<typeof AgentStateInputSchema>;

export interface AgentPauseReason {
  code: string;
  label: string;
  /** Whether time under this reason counts as work: training does, lunch does not. */
  productive: boolean;
}

/**
 * The wrap-up rule that applies to one agent, and where it came from (0111): their own override,
 * the queues they take calls for, or the account.
 */
export interface WrapUpRule {
  /** Seconds the workspace holds them after a call. Zero is none. */
  seconds: number;
  /** Whether they must say what the call was before going ready. */
  required: boolean;
  /** Where each answer came from, separately: an agent's own zero seconds does not decide the mark. */
  seconds_from: 'agent' | 'queue' | 'account';
  required_from: 'agent' | 'queue' | 'account';
}

/** The shift as the workspace needs it: what is open now, and since when. */
export interface AgentShift {
  id: string;
  /** Kept for the workspace that has not reloaded yet; `wrap_up.required` is the same answer. */
  require_wrap_up: boolean;
  wrap_up: WrapUpRule;
  extension_id: string;
  extension_number: string;
  started_at: string;
  state: AgentState;
  reason: string | null;
  state_since: string;
  reasons: AgentPauseReason[];
}

/** Nobody is logged on at this seat; the reasons come anyway, so the workspace can offer them. */
export interface NoAgentShift {
  id: null;
  reasons: AgentPauseReason[];
  require_wrap_up: boolean;
  wrap_up: WrapUpRule;
}

export type AgentShiftState = AgentShift | NoAgentShift;

/** One person's day, or one person's month: the same shape either way. */
export interface AgentAttendance {
  extension_id: string;
  extension_number: string;
  display_name: string;
  /** Seconds from logging on to logging off, summed over every shift in the period. */
  logged_on_sec: number;
  ready_sec: number;
  on_call_sec: number;
  wrap_up_sec: number;
  /** Time not ready, by reason code, and the part of it the account counts as work. */
  paused_sec: number;
  paused_productive_sec: number;
  by_reason: Array<{ reason: string; label: string; productive: boolean; seconds: number }>;
  /**
   * What their calls were, counted by the disposition's own time: a call taken at 16:58 and
   * marked at 17:02 belongs to the day it was worked on.
   */
  marked: number;
  marked_positive: number;
  marked_negative: number;
  by_disposition: Array<{ code: string; label: string; kind: DispositionGroup; calls: number }>;
  /**
   * Ready, on a call, wrapping up, and pauses the account counts as work -- over time logged on.
   * Null when nobody was logged on at all, because a percentage of nothing is not zero.
   */
  productive_sec: number;
  productive_pct: number | null;
  shifts: number;
  first_login: string | null;
  last_logout: string | null;
}

/**
 * What an account calls the outcome of a call (migration 0108).
 *
 * `kind` is the GROUP it sits under -- positive, neutral or negative (0109). Agents read the
 * groups as headings while they mark a call, and reports add up by them: a sale and a complaint
 * are both outcomes, and nobody wants them counted together. `requires_note` is for the ones
 * that mean nothing alone.
 */
/** The three groups a disposition can sit under, in the order they are shown. */
export const DISPOSITION_GROUPS = ['positive', 'neutral', 'negative'] as const;
export type DispositionGroup = (typeof DISPOSITION_GROUPS)[number];

/** How each group reads on a screen. */
export const DISPOSITION_GROUP_LABELS: Readonly<Record<DispositionGroup, string>> = {
  positive: 'Positive', neutral: 'Neutral', negative: 'Negative',
};

export interface DispositionCode {
  id: string;
  code: string;
  label: string;
  kind: DispositionGroup;
  requires_note: boolean;
  position: number;
  is_active: boolean;
}

/** A pause reason as the account keeps it, which is the editor's view of it. */
export interface PauseReasonRow {
  id: string;
  code: string;
  label: string;
  productive: boolean;
  position: number;
  is_active: boolean;
}

/** The account's own dispositions over a period, beside the per-agent rows. */
export interface DispositionTotals {
  total: number;
  positive: number;
  neutral: number;
  negative: number;
  by_code: Array<{ code: string; label: string; kind: string; calls: number }>;
}
